/**
 * FmRowActions — governed responsive row-action rail + action-overflow menu.
 *
 * The row-action portion of a dense repeated-data surface (ranked table +
 * narrative rail).
 *
 * STRUCTURE-ADAPTED: dense row actions that must not squeeze the value column;
 *   secondary actions collapse into a DS-owned overflow menu.
 * FM-COMPONENT-OWNING-LAYOUT: this primitive owns inline-vs-overflow policy,
 *   tap-target sizing, action-rail gap, keyboard reachability, and disabled/
 *   locked + confirm handling — pages never build button stacks in a cell.
 * SEMANTIC-PROPS-PAGE-PASSES: typed action descriptors (id/label/intent/icon?/
 *   disabledReason?/confirm?/onSelect) + a DS-owned overflow `policy`.
 * NOT-COPIED: no raw width thresholds, no per-table compact knobs; sizing comes
 *   only from the muem9.18 action tokens.
 *
 * Tokens consumed (muem9.18): --fm-action-rail-gap, --fm-action-target-size,
 *   --fm-action-overflow-target-size. Zero raw px/rem/hex.
 */
import React, { useState } from 'react';
import { FmButton, type FmButtonVariant } from './FmButton';
import { FmConfirm } from './FmConfirm';
import { FmMenu, type FmMenuItem } from './FmMenu';
import './FmRowActions.css';

/** Semantic action weight. The DS maps each intent to a governed button treatment. */
export type FmRowActionIntent =
  | 'primary'
  | 'secondary'
  | 'inspect'
  | 'confirm'
  | 'export'
  | 'destructive';

/** DS-owned overflow policy. NOT a width threshold — a governed inline count. */
export type FmRowActionPolicy = 'auto' | 'primary-plus-menu' | 'menu-only';

/** Confirmation copy for an action that must ask before firing (inline path). */
export interface FmRowActionConfirm {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** i18n confirm-button copy; consumer supplies (falls back to FmConfirm default). */
  confirmLabel?: React.ReactNode;
  /** i18n cancel-button copy; consumer supplies (falls back to FmConfirm default). */
  cancelLabel?: React.ReactNode;
}

/** Typed row-action descriptor. Pages pass these — never raw button JSX. */
export interface FmRowAction {
  id: string;
  /** i18n-translated label supplied by the consumer. */
  label: React.ReactNode;
  intent: FmRowActionIntent;
  icon?: React.ReactNode;
  /**
   * When set, the action is disabled/locked and this text explains why
   * (native tooltip + accessible title). Prefer a reason over silent disable.
   */
  disabledReason?: string;
  /** When set, the inline action asks for confirmation before firing. */
  confirm?: FmRowActionConfirm;
  onSelect: () => void;
  'data-testid'?: string;
}

export interface FmRowActionsProps {
  /** Row actions in consumer priority order (most important first). */
  actions: FmRowAction[];
  /** DS-owned overflow policy. Defaults to 'auto'. */
  policy?: FmRowActionPolicy;
  /** Accessible name for the action group (i18n copy from consumer). */
  label: string;
  /** Accessible name for the overflow (…) trigger (i18n copy from consumer). */
  overflowLabel: string;
  'data-testid'?: string;
}

/**
 * DS-owned inline ceiling for `policy='auto'`: at most one primary plus one
 * secondary action stay inline; everything else collapses to the overflow menu
 * (per the specimen-15 anatomy). This is a count, never a page-tunable width.
 */
const AUTO_MAX_INLINE = 2;

const INTENT_VARIANT: Record<FmRowActionIntent, FmButtonVariant> = {
  primary: 'primary',
  confirm: 'primary',
  secondary: 'text',
  // Inspect opens another view, so it reads as a link rather than a command.
  inspect: 'link',
  export: 'text',
  destructive: 'text',
};

function inlineCount(policy: FmRowActionPolicy, total: number): number {
  if (policy === 'menu-only') return 0;
  if (policy === 'primary-plus-menu') return Math.min(1, total);
  return Math.min(AUTO_MAX_INLINE, total);
}

function isDisabled(action: FmRowAction): boolean {
  return Boolean(action.disabledReason);
}

interface InlineActionButtonProps {
  action: FmRowAction;
  onRequest: (action: FmRowAction) => void;
}

function InlineActionButton({ action, onRequest }: InlineActionButtonProps): React.ReactElement {
  const disabled = isDisabled(action);
  return (
    <FmButton
      variant={INTENT_VARIANT[action.intent]}
      size="sm"
      danger={action.intent === 'destructive'}
      leadingIcon={action.icon}
      disabled={disabled}
      title={action.disabledReason}
      aria-disabled={disabled || undefined}
      data-testid={action['data-testid']}
      className="fm-row-actions__btn"
      onClick={disabled ? undefined : () => onRequest(action)}
    >
      {action.label}
    </FmButton>
  );
}

function toMenuItem(action: FmRowAction, onRequest: (action: FmRowAction) => void): FmMenuItem {
  return {
    key: action.id,
    label: action.label,
    icon: action.icon,
    disabled: isDisabled(action),
    danger: action.intent === 'destructive',
    title: action.disabledReason,
    'data-testid': action['data-testid'],
    // Route through the shared confirm gate; onRequest fires onSelect only for
    // non-confirm actions, and opens the confirmation dialog otherwise.
    onClick: () => onRequest(action),
  };
}

/**
 * FmRowActions — governed row-action rail. Renders up to a DS-owned number of
 * inline actions (by `policy`) and collapses the remainder into an accessible
 * overflow menu. Tap targets and rail gap come from the muem9.18 action tokens.
 *
 * Confirm actions are gated by a SINGLE shared FmConfirm on BOTH paths — inline
 * buttons and overflow/menu items — so a destructive/confirm action can never
 * fire without confirmation, including on the mobile/tablet collapse path.
 */
export const FmRowActions: React.FC<FmRowActionsProps> = ({
  actions,
  policy = 'auto',
  label,
  overflowLabel,
  'data-testid': dataTestId,
}) => {
  const [pendingConfirm, setPendingConfirm] = useState<FmRowAction | null>(null);

  if (actions.length === 0) return null;

  const inlineN = inlineCount(policy, actions.length);
  const inlineActions = actions.slice(0, inlineN);
  const overflowActions = actions.slice(inlineN);

  // Single confirmation gate for every path: confirm actions defer to
  // FmConfirm; everything else fires immediately. Disabled actions never fire.
  const requestSelect = (action: FmRowAction): void => {
    if (isDisabled(action)) return;
    if (action.confirm) {
      setPendingConfirm(action);
      return;
    }
    action.onSelect();
  };

  const overflowItems = overflowActions.map((action) => toMenuItem(action, requestSelect));
  const confirm = pendingConfirm?.confirm;
  const closeConfirm = (): void => setPendingConfirm(null);

  return (
    <fieldset className="fm-row-actions" aria-label={label} data-testid={dataTestId}>
      {inlineActions.length > 0 ? (
        <div className="fm-row-actions__inline">
          {inlineActions.map((action) => (
            <InlineActionButton key={action.id} action={action} onRequest={requestSelect} />
          ))}
        </div>
      ) : null}
      {overflowItems.length > 0 ? (
        <FmMenu
          items={overflowItems}
          placement="bottomRight"
          menuTestId={dataTestId ? `${dataTestId}-menu` : undefined}
        >
          <FmButton
            variant="text"
            size="sm"
            aria-label={overflowLabel}
            className="fm-row-actions__overflow-trigger"
            data-testid={dataTestId ? `${dataTestId}-overflow` : undefined}
          >
            <span aria-hidden="true">{'⋯'}</span>
          </FmButton>
        </FmMenu>
      ) : null}
      <FmConfirm
        open={pendingConfirm != null}
        title={confirm?.title ?? ''}
        description={confirm?.description}
        confirmText={confirm?.confirmLabel}
        cancelText={confirm?.cancelLabel}
        danger={pendingConfirm?.intent === 'destructive'}
        onConfirm={() => {
          pendingConfirm?.onSelect();
          closeConfirm();
        }}
        onCancel={closeConfirm}
        onOpenChange={(open) => {
          if (!open) closeConfirm();
        }}
        contentTestId={dataTestId ? `${dataTestId}-confirm` : undefined}
      >
        <span className="fm-row-actions__confirm-anchor" aria-hidden="true" />
      </FmConfirm>
    </fieldset>
  );
};

FmRowActions.displayName = 'FmRowActions';

export default FmRowActions;
